/**
 * StagePipelineAdmin.tsx — Admin > Plans > Stage Pipeline.
 *
 * Legacy's Stage Pipeline (lib/admin/admin-stage-rules.php and
 * admin-stage-details.php) is the spec, layout and drag and drop included, in
 * Prospera's components. On a computer:
 *
 *   left   the grey stage list: "+" adds a stage, Inquiry is fixed first, the
 *          rest drag to set the pipeline order (StageList)
 *   right  top to bottom: the selected stage's name and stage type
 *          (StageEditRow), the Pipeline Type bar (PipelineBar), the "Plan
 *          Types:" line, the centred pill pipeline with its "…" hide button
 *          (PipelineRow), then the read-only Add Tasks / Remove Tasks /
 *          Triggers tables (StageAutomationTables) beside Hidden Stages
 *          (HiddenStagesPanel)
 *
 * On a narrower page (L3, KTD11), measured on the page itself rather than the
 * window, because the app's side menu takes ~310px of the same window (see
 * pageLayout):
 *
 *   narrower than LIST_INLINE_MIN  the stage list moves into the off-canvas
 *             "Stages" panel, opened by a Stages button above the right side,
 *             which keeps the selected stage's name beside it; picking a stage
 *             (or "+") closes the panel, a drag reorders inside it
 *   narrower than STACK_BELOW  the pill pipeline runs top to bottom with the
 *             "…" last (PipelineRow), and the tables and Hidden Stages stack
 *
 * Nothing here is A–Z: the stage order is the pipeline itself, which the CRM
 * board's columns and every plan's progress bar follow.
 *
 * The pills show what a plan in the selected stage sees on the selected
 * pipeline. Clicking one opens its "move to" / "show arrow" popover (one at a
 * time, floating, saved at once); dragging one onto "…" or Hidden Stages hides
 * it, and back onto the row shows it. Every write is optimistic, rolls back
 * with a toast on error, then refreshes this page's queries and the CRM caches
 * (KTD14).
 *
 * Deleting a stage is a soft delete that first moves its plans, quietly, to a
 * stage the admin picks (DeleteStageDialog); it is greyed out while any
 * automation points at the stage. Show deleted lists deleted stages with
 * Restore.
 *
 * Locked (plan 2026-10-10-001, U7): the pipeline still shows, a stage still
 * opens and a pill still opens its settings, but only to read them. There is
 * no add, rename, delete, restore or drag, every field shows its value, and
 * each write path refuses, so nothing changes. Nothing says so (R18).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DndContext, DragOverlay, type DragEndEvent, type DragStartEvent } from '@dnd-kit/core';
import { keepPreviousData, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { Menu } from 'lucide-react';
import { DeleteStageDialog } from '@/components/admin/stagePipeline/DeleteStageDialog';
import { HiddenStagesPanel } from '@/components/admin/stagePipeline/HiddenStagesPanel';
import { PipelineBar, PlanTypesLine } from '@/components/admin/stagePipeline/PipelineBar';
import { PipelineRow } from '@/components/admin/stagePipeline/PipelineRow';
import { PILLS_DND_ID, PILL_DROP, pillCollision, stageIdFromPillDrag } from '@/components/admin/stagePipeline/pillDnd';
import { StageAutomationTables } from '@/components/admin/stagePipeline/StageAutomationTables';
import { StageEditRow } from '@/components/admin/stagePipeline/StageEditRow';
import { AddStageModal, PipelineTitleModal } from '@/components/admin/stagePipeline/StageFormModals';
import { StageList } from '@/components/admin/stagePipeline/StageList';
import { StagePillFace } from '@/components/admin/stagePipeline/StagePill';
import {
  CRM_CACHE_PREFIXES,
  STAGE_PIPELINE_QUERY_ROOT,
  byPipelineOrder,
  defaultRule,
  stageDeleteBlock,
  stagePipelineKeys,
  withRule,
  withStageOrder,
  type PillModel,
} from '@/components/admin/stagePipeline/stagePipelineModel';
import { AdminListEmpty } from '@/components/admin/AdminList';
import { AdminLockedFieldset } from '@/components/admin/AdminLockedFieldset';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { useAdminDragSensors } from '@/components/admin/SortableAdminRow';
import { OffCanvasPanel } from '@/components/layout/OffCanvasPanel';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { useElementSize } from '@/hooks/useElementSize';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/services/apiClient';
import {
  DEFAULT_PIPELINE_ID,
  createPipeline,
  createStage,
  getStageAutomations,
  getStagePipeline,
  getStageRules,
  renamePipeline,
  reorderStages,
  restoreStage,
  saveHiddenStages,
  saveStageRule,
  updateStage,
  type Pipeline,
  type PipelineStage,
  type StagePipelineState,
  type StageRule,
  type StageRules,
} from '@/services/stagePipelineAdmin.service';
import { toast } from '@/store/toastStore';

/** Narrower than this, the stage list moves into the Stages panel. */
const LIST_INLINE_MIN = 760;
/** Narrower than this, the pipeline runs top to bottom and the tables and Hidden Stages stack. */
const STACK_BELOW = 600;

/**
 * The layout for the page's own width. The window's width would mislead: the
 * app's side menu and admin rail take ~310px of it, so a 1000px window leaves
 * this page under 700px (useElementSize.ts). An unmeasured width (0: before
 * layout, or jsdom) is a computer.
 */
function pageLayout(pageWidth: number): { listInPanel: boolean; stacked: boolean } {
  if (pageWidth === 0) return { listInPanel: false, stacked: false };
  return { listInPanel: pageWidth < LIST_INLINE_MIN, stacked: pageWidth < STACK_BELOW };
}

export function StagePipelineAdmin() {
  const queryClient = useQueryClient();
  const [pipelineId, setPipelineId] = useState(DEFAULT_PIPELINE_ID);
  const [pickedStageId, setPickedStageId] = useState<number | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [openPillId, setOpenPillId] = useState<number | null>(null);
  const [draggingPillId, setDraggingPillId] = useState<number | null>(null);
  const [addingStage, setAddingStage] = useState(false);
  const [pipelineModal, setPipelineModal] = useState<{ pipeline: Pipeline | null } | null>(null);
  const [deleting, setDeleting] = useState<PipelineStage | null>(null);
  const pillSensors = useAdminDragSensors();
  const locked = useAdminPageLevel() === 'locked';

  // On a narrow page the stage list lives in the off-canvas panel (KTD11).
  const pageRef = useRef<HTMLDivElement>(null);
  const { listInPanel, stacked } = pageLayout(useElementSize(pageRef).width);
  const [stagesOpen, setStagesOpen] = useState(false);
  // The panel doesn't survive the move to the inline list, so it can't spring open on the way back down.
  useEffect(() => {
    if (!listInPanel) setStagesOpen(false);
  }, [listInPanel]);

  // ─── Reads ──────────────────────────────────────────────────────────────────

  const stateKey = stagePipelineKeys.state(pipelineId);
  const stateQuery = useQuery({
    queryKey: stateKey,
    queryFn: () => getStagePipeline(pipelineId),
    // Switching pipeline keeps the page up while the new Plan Types load.
    placeholderData: keepPreviousData,
  });
  const state = stateQuery.data;

  const live = useMemo(() => (state?.stages ?? []).filter((s) => s.active).sort(byPipelineOrder), [state]);
  const deleted = useMemo(() => (state?.stages ?? []).filter((s) => !s.active), [state]);
  // The picked stage while it is live, else the first stage after Inquiry (FTP opens on it).
  const selected = live.find((s) => s.id === pickedStageId) ?? live.find((s) => !s.isInquiry) ?? live[0] ?? null;
  const selectedId = selected?.id ?? null;
  // Pin the opening fallback, so a reorder never moves the selection by itself.
  useEffect(() => {
    if (pickedStageId === null && selectedId !== null) setPickedStageId(selectedId);
  }, [selectedId, pickedStageId]);

  const rulesKey = stagePipelineKeys.rules(selectedId, pipelineId);
  const rulesQuery = useQuery({
    queryKey: rulesKey,
    queryFn: () => getStageRules(selectedId as number, pipelineId),
    enabled: selectedId !== null,
  });

  const automationsQuery = useQuery({
    queryKey: stagePipelineKeys.automations(selectedId),
    queryFn: () => getStageAutomations(selectedId as number),
    enabled: selectedId !== null,
  });
  const automations = automationsQuery.data;

  const ruleFor = useCallback(
    (toStageId: number): StageRule => rulesQuery.data?.rules.find((r) => r.toStageId === toStageId) ?? defaultRule(toStageId),
    [rulesQuery.data],
  );

  const pills: PillModel[] = useMemo(
    () => live.map((s) => ({ stage: s, rule: ruleFor(s.id), selected: s.id === selectedId })),
    [live, ruleFor, selectedId],
  );
  const rowPills = pills.filter((p) => p.selected || !p.rule.hidden);
  const hiddenPills = pills.filter((p) => !p.selected && p.rule.hidden);

  // ─── Writes ─────────────────────────────────────────────────────────────────

  /** This page's queries and, by prefix, every screen that reads stages (KTD14). */
  const refresh = useCallback(() => {
    for (const key of [STAGE_PIPELINE_QUERY_ROOT, ...CRM_CACHE_PREFIXES]) void queryClient.invalidateQueries({ queryKey: [key] });
  }, [queryClient]);

  /** Show the change now, write it, and put the old value back (with a toast) if the write fails. */
  const optimistic = useCallback(
    async <T,>(key: QueryKey, update: (old: T) => T, write: () => Promise<unknown>, failure: string): Promise<boolean> => {
      await queryClient.cancelQueries({ queryKey: key });
      const before = queryClient.getQueryData<T>(key);
      if (before !== undefined) queryClient.setQueryData<T>(key, update(before));
      try {
        await write();
        refresh();
        return true;
      } catch (err) {
        queryClient.setQueryData<T>(key, before);
        toast.error(getErrorMessage(err, failure));
        void queryClient.invalidateQueries({ queryKey: [STAGE_PIPELINE_QUERY_ROOT] });
        return false;
      }
    },
    [queryClient, refresh],
  );

  function selectStage(id: number) {
    setPickedStageId(id);
    setOpenPillId(null);
  }

  /** A pick in the list; in the panel it also closes the panel, to show the stage. */
  function pickStage(id: number) {
    selectStage(id);
    setStagesOpen(false);
  }

  function openAddStage() {
    if (locked) return;
    // The Add stage dialog isn't a Radix layer: the open panel would trap its
    // focus and block its clicks, so the panel closes first.
    setStagesOpen(false);
    setAddingStage(true);
  }

  function changePipeline(id: number) {
    setPipelineId(id);
    setOpenPillId(null);
  }

  function reorder(ids: number[]) {
    if (locked) return;
    void optimistic<StagePipelineState>(stateKey, (old) => withStageOrder(old, ids), () => reorderStages(ids), 'The new order could not be saved.');
  }

  function saveName(stage: PipelineStage, name: string) {
    if (locked) return;
    void optimistic<StagePipelineState>(
      stateKey,
      (old) => ({ ...old, stages: old.stages.map((s) => (s.id === stage.id ? { ...s, name } : s)) }),
      () => updateStage(stage.id, { name }),
      'The stage name could not be saved.',
    );
  }

  function changeType(stage: PipelineStage, typeId: number) {
    if (locked) return;
    void optimistic<StagePipelineState>(
      stateKey,
      (old) => {
        const picked = old.stageTypes.find((t) => t.id === typeId);
        // The old type is free again; the new one is taken.
        const freed = stage.typeId > 0 ? [{ id: stage.typeId, name: stage.typeName }] : [];
        return {
          ...old,
          stages: old.stages.map((s) => (s.id === stage.id ? { ...s, typeId, typeName: picked?.name ?? 'Custom' } : s)),
          stageTypes: [...old.stageTypes.filter((t) => t.id !== typeId), ...freed],
        };
      },
      () => updateStage(stage.id, { typeId }),
      'The stage type could not be saved.',
    );
  }

  function changeRule(toStageId: number, patch: { moveTo?: boolean; showArrow?: boolean }) {
    if (locked || selectedId === null) return;
    const current = ruleFor(toStageId);
    const next = { moveTo: patch.moveTo ?? current.moveTo, showArrow: patch.showArrow ?? current.showArrow };
    const fromStageId = selectedId;
    const pipelineTypeId = pipelineId;
    void optimistic<StageRules>(
      rulesKey,
      (old) => withRule(old, toStageId, next),
      () => saveStageRule({ fromStageId, toStageId, pipelineTypeId, ...next }),
      'The setting could not be saved.',
    );
  }

  function setHidden(toStageId: number, hide: boolean) {
    if (locked || selectedId === null || toStageId === selectedId || !live.some((s) => s.id === toStageId)) return;
    if (ruleFor(toStageId).hidden === hide) return;
    const fromStageId = selectedId;
    const pipelineTypeId = pipelineId;
    // Every stage this one hides, in pipeline order, after the drop.
    const hiddenIds = live
      .filter((s) => s.id !== fromStageId && (s.id === toStageId ? hide : ruleFor(s.id).hidden))
      .map((s) => s.id);
    void optimistic<StageRules>(
      rulesKey,
      (old) => withRule(old, toStageId, { hidden: hide }),
      () => saveHiddenStages(fromStageId, pipelineTypeId, hiddenIds),
      hide ? 'The stage could not be hidden.' : 'The stage could not be shown.',
    );
  }

  function handlePillDragStart({ active }: DragStartEvent) {
    setDraggingPillId(stageIdFromPillDrag(active.id));
    setOpenPillId(null);
  }

  function handlePillDragEnd({ active, over }: DragEndEvent) {
    setDraggingPillId(null);
    const stageId = stageIdFromPillDrag(active.id);
    if (stageId === null || !over) return;
    if (over.id === PILL_DROP.hide || over.id === PILL_DROP.hidden) setHidden(stageId, true);
    else if (over.id === PILL_DROP.row) setHidden(stageId, false);
  }

  function handleOpenPillChange(stageId: number, open: boolean) {
    // One popover at a time: opening one replaces the other; a late close of the old one is ignored.
    setOpenPillId((cur) => (open ? stageId : cur === stageId ? null : cur));
  }

  async function handleAddStage(name: string, typeId: number) {
    const created = await createStage(name, typeId);
    // In the list at once, so selecting it doesn't wait for the refresh.
    queryClient.setQueryData<StagePipelineState>(stateKey, (old) =>
      old && !old.stages.some((s) => s.id === created.id)
        ? { ...old, stages: [...old.stages, created], stageTypes: old.stageTypes.filter((t) => t.id !== created.typeId) }
        : old,
    );
    setAddingStage(false);
    toast.success(`${created.name || name} added`);
    selectStage(created.id);
    refresh();
  }

  async function handleSavePipeline(title: string) {
    const editing = pipelineModal?.pipeline ?? null;
    if (editing) {
      await renamePipeline(editing.id, title);
      toast.success('Pipeline renamed');
    } else {
      const created = await createPipeline(title);
      toast.success(`${created.name || title} added`);
      changePipeline(created.id);
    }
    setPipelineModal(null);
    refresh();
  }

  async function handleRestore(stage: PipelineStage) {
    if (locked) return;
    try {
      await restoreStage(stage.id);
      toast.success(`${stage.name} restored`);
      refresh();
    } catch (err) {
      toast.error(getErrorMessage(err, 'The stage could not be restored.'));
    }
  }

  const deleteBlock = automations ? stageDeleteBlock(automations.blockers, automations.pendingPlanTasks) : null;

  function handleDeleteClick() {
    if (locked || !selected) return;
    if (deleteBlock) {
      // Greyed out: the click says why (Admin standard).
      toast.error(deleteBlock);
      return;
    }
    setDeleting(selected);
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  const draggingPill = draggingPillId === null ? null : pills.find((p) => p.stage.id === draggingPillId) ?? null;
  const pillCallbacks = { openPillId, onOpenPillChange: handleOpenPillChange, onRuleChange: changeRule };

  const pillArea = (
    <>
      <PipelineRow pills={rowPills} loading={!rulesQuery.data} vertical={stacked} {...pillCallbacks} />
      {/* Side by side; stacked on a narrow page. */}
      <div className={cn('flex items-start gap-4 px-4 pb-6', stacked && 'flex-col items-stretch')}>
        <StageAutomationTables automations={automations} />
        <HiddenStagesPanel pills={rulesQuery.data ? hiddenPills : []} className={stacked ? 'w-full' : undefined} {...pillCallbacks} />
      </div>
    </>
  );

  // One element, inline or in the panel, so the two places can't drift apart.
  const stageList = (
    <StageList
      stages={live}
      deleted={deleted}
      selectedId={selectedId}
      onSelect={pickStage}
      onAdd={openAddStage}
      onReorder={reorder}
      onRestore={(s) => void handleRestore(s)}
      showDeleted={showDeleted}
      onShowDeletedChange={setShowDeleted}
    />
  );

  return (
    <div ref={pageRef} data-testid="stage-pipeline-page" className="flex h-full min-h-0 flex-col overflow-x-hidden max-md:h-auto max-md:pb-16">
      <div className="shrink-0 px-6 pt-6 max-md:px-3">
        <PageHeader
          title="Stage Pipeline"
          subtitle={
            locked
              ? 'Pick a stage and a pipeline, and click a pill to see what a plan in that stage can move to.'
              : 'Drag stages into pipeline order. Pick a stage and a pipeline, click a pill to set what a plan in that stage can move to, and drag pills onto … to hide them.'
          }
          className="mb-4"
        />
      </div>

      <div className="relative mx-6 mb-6 flex min-h-0 flex-1 overflow-hidden rounded-lg border border-slate-200 bg-white max-md:mx-3">
        {stateQuery.isLoading ? (
          // flex-1: the card is a flex row, and without it the spinner shrinks to its own width at the left.
          <div className="flex-1" data-testid="stage-pipeline-loading">
            <SpinnerOverlay label="Loading the stage pipeline…" />
          </div>
        ) : !state ? (
          <div className="flex-1">
            <AdminListEmpty title="The stage pipeline could not be loaded" hint={getErrorMessage(stateQuery.error, 'Try again in a moment.')} />
          </div>
        ) : (
          <>
            {!listInPanel && stageList}
            {listInPanel && (
              <OffCanvasPanel id="stage-pipeline-stages" title="Stages" side="left" isOpen={stagesOpen} onClose={() => setStagesOpen(false)}>
                {stageList}
              </OffCanvasPanel>
            )}

            <div data-testid="stage-pipeline-main" className="relative flex min-w-0 flex-1 flex-col overflow-y-auto">
              {listInPanel && (
                <div data-testid="stages-bar" className="flex shrink-0 items-center gap-3 border-b border-slate-200 bg-white px-3 py-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<Menu className="h-4 w-4" />}
                    aria-haspopup="dialog"
                    aria-expanded={stagesOpen}
                    onClick={() => setStagesOpen(true)}
                  >
                    Stages
                  </Button>
                  {selected && <span className="min-w-0 truncate text-sm font-semibold text-slate-800">{selected.name}</span>}
                </div>
              )}
              {/* The stage first, then the pipeline its pills are shown on. */}
              {selected && (
                <AdminLockedFieldset>
                  <StageEditRow
                    key={selected.id}
                    stage={selected}
                    stageTypes={state.stageTypes}
                    onSaveName={(name) => saveName(selected, name)}
                    onChangeType={(typeId) => changeType(selected, typeId)}
                    deleteBlockedReason={deleteBlock}
                    deleteChecking={!automations}
                    onDelete={handleDeleteClick}
                  />
                </AdminLockedFieldset>
              )}
              <PipelineBar
                pipelines={state.pipelines}
                pipelineId={pipelineId}
                onPipelineChange={changePipeline}
                onRename={(p) => setPipelineModal({ pipeline: p })}
                onAdd={() => setPipelineModal({ pipeline: null })}
              />
              <PlanTypesLine planTypes={stateQuery.isPlaceholderData ? null : state.planTypes} />

              {selected && locked ? (
                // Locked: nothing drags, so no drag context (or its drag instructions) at all.
                pillArea
              ) : selected ? (
                <DndContext
                  id={PILLS_DND_ID}
                  sensors={pillSensors}
                  collisionDetection={pillCollision}
                  onDragStart={handlePillDragStart}
                  onDragEnd={handlePillDragEnd}
                  onDragCancel={() => setDraggingPillId(null)}
                >
                  {pillArea}
                  {/* No drop animation: the pill just lands where it was dropped. */}
                  <DragOverlay dropAnimation={null}>{draggingPill ? <StagePillFace pill={draggingPill} /> : null}</DragOverlay>
                </DndContext>
              ) : (
                <AdminListEmpty
                  title="No stages yet"
                  hint={locked ? undefined : listInPanel ? 'Tap Stages, then the + at the top of the list.' : 'Add a stage with the + at the top of the list.'}
                />
              )}
            </div>
          </>
        )}
      </div>

      {addingStage && state && !locked && (
        <AddStageModal stageTypes={state.stageTypes} onSave={handleAddStage} onClose={() => setAddingStage(false)} />
      )}
      {pipelineModal && state && !locked && (
        <PipelineTitleModal
          key={pipelineModal.pipeline?.id ?? 'new'}
          pipeline={pipelineModal.pipeline}
          pipelines={state.pipelines}
          onSave={handleSavePipeline}
          onClose={() => setPipelineModal(null)}
        />
      )}
      {deleting && !locked && (
        <DeleteStageDialog
          stage={deleting}
          targets={live.filter((s) => s.id !== deleting.id)}
          warnings={automations?.stageId === deleting.id ? automations.warnings : []}
          onClose={() => setDeleting(null)}
          onDeleted={(target, moved) => {
            setDeleting(null);
            toast.success(`${deleting.name} deleted. ${moved} ${moved === 1 ? 'plan' : 'plans'} moved to ${target.name}.`);
            selectStage(target.id);
            refresh();
          }}
          onBlocked={() => {
            setDeleting(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

export default StagePipelineAdmin;
